// src/components/jarvisly-components/others/CepInput.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useMemo, useRef } from 'react';
import { Input } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { applyNumberMask, returnOnlyNumbers } from 'utils/helpers';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const DEFAULT_MASK = '99999-999'; // BR CEP

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CepInput = React.forwardRef(
  (
    {
      // do not forward props that toggle AntD suffix dynamically
      allowClear, // avoid suffix mount/unmount
      prefix,
      suffix,
      value,
      type,
      status, // '', 'validating', 'success', 'error'
      loading, // boolean
      mask = DEFAULT_MASK, // override for other countries (e.g. PT '9999-999')
      ...props
    },
    ref
  ) => {
    // ---------------------------------------------------------------------------
    // 1. PROPS, CONTEXTS & GLOBAL STATE
    // ---------------------------------------------------------------------------

    // ---------------------------------------------------------------------------
    // 2. HOOKS & REFS
    // ---------------------------------------------------------------------------
    // keep prefix stable to avoid DOM restructure
    const stablePrefixRef = useRef(prefix ?? null);

    // ---------------------------------------------------------------------------
    // 3. LOCAL VARIABLES I
    // ---------------------------------------------------------------------------
    // mask value without switching controlled/uncontrolled state
    const masked = useMemo(() => {
      const raw = returnOnlyNumbers(value);
      return raw ? applyNumberMask(raw, mask) : (value ?? '');
    }, [value, mask]);

    // build a static suffix container that never mounts/unmounts and stacks icons
    const Suffix = useMemo(() => {
      const isValidating = status === 'validating' || !!loading;
      const isSuccess = status === 'success';
      const isError = status === 'error';

      // container keeps a fixed box; icons are absolutely centered on top of each other
      const wrapperStyle = {
        display: 'inline-flex',
        alignItems: 'center',
      };

      const boxStyle = {
        position: 'relative',
        width: 18,
        height: 18,
      };

      const iconCommon = {
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
      };

      return (
        <span style={wrapperStyle}>
          <span style={boxStyle}>
            <span style={{ ...iconCommon, visibility: isValidating ? 'visible' : 'hidden' }}>
              <LoadingOutlined />
            </span>
            <span style={{ ...iconCommon, visibility: isSuccess ? 'visible' : 'hidden' }}>
              <CheckCircleOutlined />
            </span>
            <span style={{ ...iconCommon, visibility: isError ? 'visible' : 'hidden' }}>
              <CloseCircleOutlined />
            </span>
          </span>
          {/* keep any external suffix content stable if provided, after the icon box */}
          <span style={{ visibility: suffix ? 'visible' : 'hidden' }}>{suffix || null}</span>
        </span>
      );
    }, [status, loading, suffix]);

    // ---------------------------------------------------------------------------
    // 4. LOCAL STATE
    // ---------------------------------------------------------------------------

    // ---------------------------------------------------------------------------
    // 5. EFFECTS
    // ---------------------------------------------------------------------------

    // ---------------------------------------------------------------------------
    // 6. HANDLERS
    // ---------------------------------------------------------------------------

    // ---------------------------------------------------------------------------
    // 7. LOCAL VARIABLES II
    // ---------------------------------------------------------------------------

    // ---------------------------------------------------------------------------
    // 8. UI
    // ---------------------------------------------------------------------------
    return (
      <Input
        {...props}
        ref={ref}
        type={type}
        value={masked}
        prefix={stablePrefixRef.current}
        suffix={Suffix}
        // never pass allowClear to avoid AntD toggling suffix container
      />
    );
  }
);

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CepInput;

// *******************************************************************************
// HELPERS
// *******************************************************************************
